import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const deleteTaskVariablesSchema = z.object({
  id: z.coerce.number(),
});

export type DeleteTaskVariables = z.infer<typeof deleteTaskVariablesSchema>;

export async function deleteTaskFn(
  axiosInstance: Promise<AxiosInstance>,
  data: DeleteTaskVariables,
) {
  const instance = await axiosInstance;
  return instance.delete(`/tasks/${data.id}`);
}

export function useDeleteTask() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteTaskVariables>({
    mutationFn: variables => deleteTaskFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, DeleteTaskVariables, 'deleteTask'>(
    'deleteTask',
    mutation,
  );
}
